<template>
    <div class="editor" :class="{ 'tema-oscuro': $vuetify.theme.dark }">
        <div v-if="editor" class="barra">
            <v-btn
                v-for="boton in botones"
                :key="boton.accion"
                icon
                small
                :title="boton.titulo"
                :color="estaActivo(boton) ? 'primary' : undefined"
                @click="ejecutar(boton.accion)"
            >
                <v-icon small>{{ boton.icono }}</v-icon>
            </v-btn>

            <v-btn
                v-for="opcion in ALINEACIONES"
                :key="`texto-${opcion.valor}`"
                icon
                small
                :title="`Texto a la ${opcion.titulo.toLowerCase()}`"
                :color="editor.isActive({ textAlign: opcion.valor }) ? 'primary' : undefined"
                @click="alinearTexto(opcion.valor)"
            >
                <v-icon small>{{ opcion.icono }}</v-icon>
            </v-btn>

            <v-divider vertical class="mx-1"></v-divider>

            <!-- Enlace: se pide la URL en un menú y no con un prompt del
                 navegador, que algunos bloquean dentro de un modal. -->
            <v-menu v-model="menuEnlace" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn
                        icon
                        small
                        title="Insertar enlace"
                        :color="editor.isActive('link') ? 'primary' : undefined"
                        v-on="on"
                        @click="prepararEnlace"
                    >
                        <v-icon small>mdi-link-variant</v-icon>
                    </v-btn>
                </template>
                <v-card class="pa-3" width="320">
                    <v-text-field
                        v-model="urlEnlace"
                        label="Dirección del enlace"
                        placeholder="https://"
                        dense
                        hide-details
                        autofocus
                        @keyup.enter="aplicarEnlace"
                    ></v-text-field>
                    <div class="d-flex justify-end mt-3">
                        <v-btn small text @click="quitarEnlace">Quitar</v-btn>
                        <v-btn small color="primary" @click="aplicarEnlace">Aplicar</v-btn>
                    </div>
                </v-card>
            </v-menu>

            <!-- Color: paleta corta en vez de selector completo. -->
            <v-menu offset-y>
                <template v-slot:activator="{ on }">
                    <v-btn icon small title="Color del texto" v-on="on">
                        <v-icon small>mdi-format-color-text</v-icon>
                    </v-btn>
                </template>
                <v-card class="pa-2">
                    <div class="paleta">
                        <button
                            v-for="color in colores"
                            :key="color"
                            class="muestra"
                            :style="{ backgroundColor: color }"
                            :title="color"
                            @click="aplicarColor(color)"
                        ></button>
                    </div>
                    <v-btn small text block class="mt-1" @click="quitarColor">
                        Sin color
                    </v-btn>
                </v-card>
            </v-menu>

            <v-btn icon small title="Insertar imagen" @click="elegirImagen">
                <v-icon small>mdi-image-outline</v-icon>
            </v-btn>

            <!-- Las acciones de tabla van en un panel aparte: son once y en la
                 barra ocuparian once huecos que el resto del tiempo estarian
                 apagados, porque casi todas solo sirven dentro de una tabla. -->
            <v-menu offset-y :close-on-content-click="false">
                <template v-slot:activator="{ on }">
                    <v-btn
                        icon
                        small
                        title="Tabla"
                        :color="editor.isActive('table') ? 'primary' : undefined"
                        v-on="on"
                    >
                        <v-icon small>mdi-table</v-icon>
                    </v-btn>
                </template>

                <v-card class="pa-3" width="290">
                    <v-btn
                        small
                        block
                        elevation="0"
                        color="primary"
                        @click="insertarTabla"
                    >
                        <v-icon left small>mdi-table-plus</v-icon>
                        Insertar tabla
                    </v-btn>

                    <div v-if="editor.isActive('table')">
                        <v-divider class="my-3"></v-divider>

                        <div class="fila-control">
                            <span class="etiqueta">Fila</span>
                            <v-btn icon small title="Añadir fila" @click="enTabla('addRowAfter')">
                                <v-icon small>mdi-table-row-plus-after</v-icon>
                            </v-btn>
                            <v-btn icon small title="Quitar fila" @click="enTabla('deleteRow')">
                                <v-icon small>mdi-table-row-remove</v-icon>
                            </v-btn>
                        </div>

                        <div class="fila-control">
                            <span class="etiqueta">Columna</span>
                            <v-btn icon small title="Añadir columna" @click="enTabla('addColumnAfter')">
                                <v-icon small>mdi-table-column-plus-after</v-icon>
                            </v-btn>
                            <v-btn icon small title="Quitar columna" @click="enTabla('deleteColumn')">
                                <v-icon small>mdi-table-column-remove</v-icon>
                            </v-btn>
                        </div>

                        <v-divider class="my-3"></v-divider>

                        <div class="fila-control">
                            <span class="etiqueta">Ancho</span>
                            <v-btn
                                v-for="ancho in ANCHOS"
                                :key="ancho"
                                x-small
                                :outlined="atributoTabla('width') !== ancho"
                                :color="atributoTabla('width') === ancho ? 'primary' : undefined"
                                class="mr-1"
                                @click="fijarAtributo('width', ancho)"
                            >
                                {{ ancho }}
                            </v-btn>
                        </div>

                        <div class="fila-control">
                            <span class="etiqueta">Alinear</span>
                            <v-btn
                                v-for="opcion in ALINEACIONES"
                                :key="opcion.valor"
                                icon
                                small
                                :title="opcion.titulo"
                                :color="atributoTabla('align') === opcion.valor ? 'primary' : undefined"
                                @click="fijarAtributo('align', opcion.valor)"
                            >
                                <v-icon small>{{ opcion.icono }}</v-icon>
                            </v-btn>
                        </div>

                        <v-divider class="my-3"></v-divider>

                        <div class="fila-control">
                            <span class="etiqueta">Borde</span>
                            <v-btn
                                v-for="grosor in GROSORES_BORDE"
                                :key="grosor"
                                x-small
                                :outlined="(atributoTabla('border') || '0') !== grosor"
                                :color="(atributoTabla('border') || '0') === grosor ? 'primary' : undefined"
                                class="mr-1"
                                @click="fijarBorde(grosor)"
                            >
                                {{ grosor }}
                            </v-btn>
                        </div>

                        <v-checkbox
                            :input-value="editor.isActive('tableHeader')"
                            label="Primera fila como encabezado"
                            dense
                            hide-details
                            class="mt-0"
                            @change="enTabla('toggleHeaderRow')"
                        ></v-checkbox>

                        <v-divider class="my-3"></v-divider>

                        <v-btn small text block color="error" @click="enTabla('deleteTable')">
                            Eliminar tabla
                        </v-btn>
                    </div>
                </v-card>
            </v-menu>

            <v-divider vertical class="mx-1"></v-divider>

            <v-btn icon small title="Deshacer" @click="editor.chain().focus().undo().run()">
                <v-icon small>mdi-undo</v-icon>
            </v-btn>
            <v-btn icon small title="Rehacer" @click="editor.chain().focus().redo().run()">
                <v-icon small>mdi-redo</v-icon>
            </v-btn>
        </div>

        <editor-content class="contenido contenido-html" :editor="editor" @click.native="enfocar" />

        <input
            ref="archivo"
            type="file"
            :accept="FORMATOS_IMAGEN.join(',')"
            class="d-none"
            @change="insertarImagen"
        />
    </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import TextStyle from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TableBase from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TextAlign from "@tiptap/extension-text-align";

// Las imágenes se incrustan en base64 dentro del propio HTML, y ese HTML lo
// sirve la portada sin sesión: una foto de 400 KB son ~533 KB que se descarga
// cada visitante de la pantalla de login. El tope se repite en el backend
// (2 MB por sección, contando todo el contenido), pero avisar aquí es lo que
// permite decir POR QUÉ se rechaza en vez de mostrar un error del servidor.
const TAMANO_MAXIMO_IMAGEN = 400 * 1024;

// Los mismos formatos que acepta el saneador del backend. SVG queda fuera a
// propósito: puede traer un <script> dentro. Si se colara, el backend lo
// borraría y la imagen desaparecería sin explicación.
const FORMATOS_IMAGEN = ["image/png", "image/jpeg", "image/gif", "image/webp"];

// Grosores de borde que acepta el saneador del backend. El 0 no esta: "sin
// borde" se guarda quitando el atributo, no poniendolo en cero.
const GROSORES_BORDE = ["1", "2", "3", "4"];

// Tiptap no conoce el atributo `border` de HTML4, asi que se le ensena. Se usa
// ese y no una clase propia porque es el que produce el CKEditor del admin de
// Django: una tabla hecha alli conserva su borde aunque despues se reedite
// desde aqui. El backend valida el grosor contra la misma lista cerrada.
const Table = TableBase.extend({
    addAttributes() {
        const heredados = this.parent ? this.parent() : {};
        return {
            ...heredados,
            border: {
                default: null,
                parseHTML: (elemento) =>
                    GROSORES_BORDE.includes(elemento.getAttribute("border"))
                        ? elemento.getAttribute("border")
                        : null,
                renderHTML: (atributos) =>
                    atributos.border ? { border: atributos.border } : {},
            },
            // Ancho en porcentaje y de una lista cerrada: un ancho libre en
            // pixeles se sale de la pantalla en un telefono. El backend valida
            // la misma lista.
            width: {
                default: null,
                parseHTML: (elemento) => elemento.getAttribute("width"),
                renderHTML: (atributos) =>
                    atributos.width ? { width: atributos.width } : {},
            },
            align: {
                default: null,
                parseHTML: (elemento) => elemento.getAttribute("align"),
                renderHTML: (atributos) =>
                    atributos.align ? { align: atributos.align } : {},
            },
        };
    },
});

export default {
    components: { EditorContent },

    props: {
        value: {
            type: String,
            default: "",
        },
    },

    data: () => ({
        editor: null,
        menuEnlace: false,
        urlEnlace: "",
        FORMATOS_IMAGEN,
        // Solo las funciones necesarias: lo que no está aquí tampoco lo acepta
        // la lista de etiquetas del backend.
        botones: [
            { accion: "toggleBold", marca: "bold", icono: "mdi-format-bold", titulo: "Negrita" },
            { accion: "toggleItalic", marca: "italic", icono: "mdi-format-italic", titulo: "Cursiva" },
            { accion: "toggleUnderline", marca: "underline", icono: "mdi-format-underline", titulo: "Subrayado" },
            { accion: "toggleBulletList", marca: "bulletList", icono: "mdi-format-list-bulleted", titulo: "Lista con viñetas" },
            { accion: "toggleOrderedList", marca: "orderedList", icono: "mdi-format-list-numbered", titulo: "Lista numerada" },
        ],
        // La misma lista cerrada que valida el backend.
        ANCHOS: ["25%", "50%", "75%", "100%"],
        // El 0 es "sin borde": se guarda quitando el atributo, no en cero.
        GROSORES_BORDE: ["0", ...GROSORES_BORDE],
        ALINEACIONES: [
            { valor: "left", titulo: "Izquierda", icono: "mdi-format-align-left" },
            { valor: "center", titulo: "Centrada", icono: "mdi-format-align-center" },
            { valor: "right", titulo: "Derecha", icono: "mdi-format-align-right" },
        ],
        colores: [
            "#000000",
            "#616161",
            "#E65100",
            "#1565C0",
            "#2E7D32",
            "#C62828",
            "#6A1B9A",
        ],
    }),

    watch: {
        // El padre puede cargar contenido después de montar el editor (por
        // ejemplo al traer la sección de la API). Se compara antes de escribir
        // para no reiniciar el documento en cada tecla, que movería el cursor
        // al principio.
        value(nuevo) {
            if (this.editor && nuevo !== this.editor.getHTML()) {
                this.editor.commands.setContent(nuevo || "", false);
            }
        },
    },

    mounted() {
        this.editor = new Editor({
            content: this.value || "",
            extensions: [
                StarterKit.configure({
                    // Fuera lo que el backend no acepta: sin botón que las
                    // inserte, estas solo entrarían al pegar desde otro sitio y
                    // el saneador las borraría igual.
                    blockquote: false,
                    codeBlock: false,
                    code: false,
                    horizontalRule: false,
                    // Sin botón propio, pero activos para que el contenido que
                    // ya tiene títulos se pueda editar sin perderlos.
                    heading: { levels: [2, 3] },
                }),
                Underline,
                Link.configure({
                    openOnClick: false,
                    HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
                }),
                // allowBase64 es false por defecto y su parser ignora
                // `<img src="data:...">`: la imagen se ve al insertarla, pero
                // desaparece al releer el contenido, y volver a guardar la
                // borraria de la base.
                Image.configure({ allowBase64: true }),
                TextStyle,
                Color,
                // Sin redimensionado: las columnas se reparten el ancho por
                // igual. Permitirlo obligaria a guardar los anchos en el
                // contenido, y eso son estilos dentro del texto.
                // Alinea parrafos y titulos, tambien dentro de una celda: la
                // celda contiene parrafos, asi que alinear el parrafo alcanza.
                TextAlign.configure({ types: ["paragraph", "heading"] }),
                Table.configure({ resizable: false }),
                TableRow,
                TableHeader,
                TableCell,
            ],
            editorProps: {
                // Tiptap no hace nada con una imagen pegada: hay que leerla del
                // portapapeles y convertirla igual que si se hubiera elegido
                // desde el boton.
                handlePaste: (vista, evento) => {
                    const portapapeles = evento.clipboardData;
                    if (!portapapeles || !portapapeles.files.length) {
                        return false;
                    }
                    const imagenes = Array.prototype.slice
                        .call(portapapeles.files)
                        .filter((archivo) => archivo.type.indexOf("image/") === 0);
                    if (!imagenes.length) {
                        return false;
                    }
                    evento.preventDefault();
                    imagenes.forEach((archivo) => this.insertarArchivo(archivo));
                    return true;
                },
                // Arrastrar y soltar pasa por el mismo camino.
                handleDrop: (vista, evento) => {
                    const arrastrado = evento.dataTransfer;
                    if (!arrastrado || !arrastrado.files.length) {
                        return false;
                    }
                    const imagenes = Array.prototype.slice
                        .call(arrastrado.files)
                        .filter((archivo) => archivo.type.indexOf("image/") === 0);
                    if (!imagenes.length) {
                        return false;
                    }
                    evento.preventDefault();
                    imagenes.forEach((archivo) => this.insertarArchivo(archivo));
                    return true;
                },
            },
            onUpdate: () => {
                this.$emit("input", this.editor.getHTML());
            },
        });
    },

    beforeDestroy() {
        if (this.editor) {
            this.editor.destroy();
        }
    },

    methods: {
        estaActivo(boton) {
            return this.editor.isActive(boton.marca);
        },

        ejecutar(accion) {
            this.editor.chain().focus()[accion]().run();
        },

        prepararEnlace() {
            this.urlEnlace = this.editor.getAttributes("link").href || "";
        },

        aplicarEnlace() {
            const url = this.urlEnlace.trim();
            if (!url) {
                this.quitarEnlace();
                return;
            }
            this.editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({ href: url })
                .run();
            this.menuEnlace = false;
        },

        quitarEnlace() {
            this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
            this.menuEnlace = false;
        },

        aplicarColor(color) {
            this.editor.chain().focus().setColor(color).run();
        },

        quitarColor() {
            this.editor.chain().focus().unsetColor().run();
        },

        alinearTexto(valor) {
            // Volver a pulsar la alineacion activa la quita.
            if (this.editor.isActive({ textAlign: valor })) {
                this.editor.chain().focus().unsetTextAlign().run();
                return;
            }
            this.editor.chain().focus().setTextAlign(valor).run();
        },

        atributoTabla(nombre) {
            // Se consulta en cada render y no desde un computed: los atributos
            // del editor no son reactivos para Vue, pero la barra se repinta en
            // cada transaccion.
            if (!this.editor) {
                return null;
            }
            return this.editor.getAttributes("table")[nombre] || null;
        },

        fijarBorde(grosor) {
            this.editor
                .chain()
                .focus()
                .updateAttributes("table", { border: grosor === "0" ? null : grosor })
                .run();
        },

        fijarAtributo(nombre, valor) {
            // Volver a pulsar la opcion activa la quita.
            const actual = this.atributoTabla(nombre);
            const nuevo = actual === valor ? null : valor;
            this.editor
                .chain()
                .focus()
                .updateAttributes("table", { [nombre]: nuevo })
                .run();
        },

        insertarTabla() {
            // Una fila y cuatro columnas, sin encabezado: es la forma de la tira
            // de contactos, que es para lo que se usan aqui.
            this.editor
                .chain()
                .focus()
                .insertTable({ rows: 1, cols: 4, withHeaderRow: false })
                .run();
        },

        enTabla(comando) {
            this.editor.chain().focus()[comando]().run();
        },

        enfocar() {
            // El area editable la dibuja ProseMirror y solo llega hasta donde
            // llega el texto. Sin esto, un clic en el espacio de abajo no enfoca
            // nada y parece que el editor no recibe lo que se escribe.
            if (this.editor && !this.editor.isFocused) {
                this.editor.commands.focus();
            }
        },

        elegirImagen() {
            this.$refs.archivo.click();
        },

        insertarImagen(evento) {
            const archivo = evento.target.files[0];
            // Se limpia el input para que elegir dos veces el mismo archivo
            // vuelva a disparar el change.
            evento.target.value = "";
            if (archivo) {
                this.insertarArchivo(archivo);
            }
        },

        insertarArchivo(archivo) {
            // Camino unico del boton, del pegado y del arrastre: los tres tienen
            // que pasar por las mismas comprobaciones.
            if (!FORMATOS_IMAGEN.includes(archivo.type)) {
                this.avisar("Solo se pueden insertar imágenes PNG, JPG, GIF o WEBP.");
                return;
            }

            if (archivo.size > TAMANO_MAXIMO_IMAGEN) {
                const peso = Math.round(archivo.size / 1024);
                this.avisar(
                    `La imagen pesa ${peso} KB y el máximo es 400 KB. ` +
                        `Se incrusta dentro del texto, así que una imagen grande ` +
                        `hace más lenta la pantalla de inicio para todos.`
                );
                return;
            }

            const lector = new FileReader();
            lector.onload = () => {
                this.editor.chain().focus().setImage({ src: lector.result }).run();
            };
            lector.onerror = () => {
                this.avisar("No se pudo leer la imagen.");
            };
            lector.readAsDataURL(archivo);
        },

        avisar(mensaje) {
            this.$store.dispatch("alert/error", mensaje);
        },
    },
};
</script>

<style lang="scss" scoped>
.editor {
    border: 1px solid rgba(0, 0, 0, 0.25);
    border-radius: 6px;
    overflow: hidden;

    &.tema-oscuro {
        border-color: rgba(255, 255, 255, 0.3);
    }
}

.barra {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 4px 6px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.15);

    .tema-oscuro & {
        border-bottom-color: rgba(255, 255, 255, 0.2);
    }
}

.contenido {
    padding: 8px 12px;
    max-height: 55vh;
    overflow-y: auto;
    cursor: text;
}

.paleta {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
}

.fila-control {
    display: flex;
    align-items: center;
    min-height: 34px;

    .etiqueta {
        width: 70px;
        font-size: 0.8rem;
        opacity: 0.75;
    }
}

.muestra {
    width: 26px;
    height: 26px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.3);
    cursor: pointer;
}
</style>

<style lang="scss">
/* Sin scope: el contenido lo renderiza ProseMirror dentro del editor y no
   lleva el atributo de scope de Vue. */
.editor .ProseMirror {
    outline: none;
    /* El alto minimo va aqui y no en el contenedor: este es el contenteditable,
       asi que es lo unico que hace clicable el espacio en blanco de abajo. */
    min-height: 220px;

    > * + * {
        margin-top: 0.6em;
    }

    ul,
    ol {
        padding-left: 1.2rem;
    }

    img {
        max-width: 100%;
        height: auto;
    }

    a {
        text-decoration: underline;
    }

    /* Lo unico propio del editor: una guia para ver donde empieza cada celda
       cuando la tabla no lleva borde. Todo lo demas --anchos, alineacion,
       grosores, encabezado-- sale de contenido-editable.scss, que es el mismo
       que usa la vista del alumno. */
    table:not([border]) {
        td,
        th {
            border: 1px dashed rgba(128, 128, 128, 0.45);
        }
    }

    .selectedCell {
        position: relative;

        &:after {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(128, 128, 128, 0.2);
            pointer-events: none;
        }
    }
}
</style>
